<template>
  <c-box
    class="privilege"
    :loading="ordering"
    :header-background-variant="lead ? 'warning' : (connected ? 'success' : 'danger')"
    shadow-hover
  >
    <template v-slot:header>
      <div style="text-align: center">
        <component
          :is="iconComponent"
          class="privilege-icon"
        />
      </div>
    </template>
    <p
      class="text-bold"
      v-text="title"
    />
    <p v-text="description" />
    <div
      v-if="period"
      class="privilege-badge"
      :class="[ periodBadgeVariant, periodBadgeWidthClass ]"
    >
      1 раз в {{ { months: period } | duration('humanize') }}
    </div>
    <button
      v-if="!connected"
      class="btn background-white border-danger text-danger privilege-order-btn"
      :disabled="ordering || !domainSelected"
      @click="order()"
    >
      Заказать
    </button>
  </c-box>
</template>

<script>
import CBox from '../../ui/CBox'
import CLoading from '../../ui/CLoadingOverlay'
import apiService from '../../../../../Modules/Seo/Resources/assets/js/apiService'
import modal from '../../../mixins/modal'
import { mapActions, mapMutations, mapState } from 'vuex'
import Notification from '../../../notification'

/**
 * Блок привилегии.
 */
export default {
  name: 'Privilege',
  components: {
    CBox,
    CLoading
  },
  mixins: [modal],
  props: {
    /**
     * Подключена привилегия или нет.
     */
    connected: {
      type: Boolean,
      default: true
    },
    /**
     * Является ли привилегия ключевой (устанавливает фон блока с иконкой).
     */
    lead: {
      type: Boolean,
      default: false
    },
    /**
     * Символьное имя привилегии. Будет использовано для вывода иконки по имени файла.
     * Путь к файлам-иконкам: @/assets/images/special-offer/privileges/.
     */
    symbolName: {
      type: String,
      required: true
    },
    /**
     * Текст заголовка.
     */
    title: {
      type: String,
      default: ''
    },
    /**
     * Текст описания.
     */
    description: {
      type: String,
      default: ''
    },
    /**
     * Периодичность в месяцах.
     */
    period: {
      type: Number,
      default: 0
    },
    /**
     * Фоновый цвет бэйджа периодичности.
     *
     * @values green, red
     */
    periodBadgeVariant: {
      type: String,
      default: 'green'
    },
    /**
     * Должен ли бэйдж периодичности быть во всю ширину.
     */
    periodBadgeFullWidth: {
      type: Boolean,
      default: true
    },
    /**
     * Скрыть ли кнопку заказа привилегии.
     */
    hideBtn: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      ordering: false
    }
  },
  computed: {
    /**
     * Возвращает компонент-иконку.
     *
     * @returns {function(): *}
     */
    iconComponent () {
      return (resolve) => {
        import('../../../../img/special-offer/privileges/' + this.symbolName + '.svg?inline')
          .then(loadedComponent => {
            resolve(loadedComponent)
          })
          .catch(() => {
            import('../../../../img/special-offer/privileges/default.svg?inline').then(loadedComponent => {
              resolve(loadedComponent)
            })
          })
      }
    },
    /**
     * Возвращает класс ширины бэйджа периодичности.
     *
     * @returns {string}
     */
    periodBadgeWidthClass () {
      return this.periodBadgeFullWidth ? 'full-width' : ''
    },
    ...mapState({
      domainSelected: state => state.seo.client.domains.selected
    })
  },
  methods: {
    /**
     * Заказ привилегии. Для некоторых привилегий будет показано модальное окно для ввода дополнительной информации.
     */
    order () {
      // Некоторые привилегии при заказе требуют ввода дополнительной информации
      if (['birth', 'adding-content'].includes(this.symbolName)) {
        this.setFormsLoyaltyProgramSymbolName(this.symbolName)
        this.showModal(this.$getConst('PRIVILEGE_ORDER_MODAL_ID'))
      } else {
        this.ordering = true

        apiService.postFormsLoyaltyProgram(this.domainSelected, this.symbolName)
          .then(() => {
            this.connectPrivilege(this.symbolName)

            this.ordering = false

            Notification.notifySuccess({
              title: 'Привилегия заказана'
            })
          })
          .catch(() => {
            this.ordering = false

            Notification.notifyError({
              title: 'Ошибка',
              content: 'Не удалось заказать привилегию.'
            })
          })
      }
    },
    ...mapMutations({
      setFormsLoyaltyProgramSymbolName: 'seo/forms/loyaltyProgram/setSymbolName'
    }),
    ...mapActions({
      connectPrivilege: 'seo/forms/loyaltyProgramMap/connectPrivilege'
    })
  }
}
</script>

<style lang="scss" scoped>
@import "./resources/css/_variables.scss";

.privilege {
  height: 100%;
}

.privilege-icon {
  height: 70px;
  width: auto;
  margin: 30px;
}

.privilege-icon * {
  fill: white;
  --color-fill: #000;
}

.privilege-badge {
  border-radius: $borderRadiusSmall;
  color: white;
  font-weight: bold;
  text-align: center;
  padding: 5px;
  margin-bottom: 10px;
  display: inline-block;
}
.privilege-badge.full-width {
  display: block;
}

.privilege-badge.green {
  background-color: $textSuccessColor;
}

.privilege-badge.red {
  background-color: $textDangerColor;
}

.privilege-order-btn {
  width: 100%;
}
</style>
